import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import useApiCall from '../../../services/api/useApiCall';
import { buildApiConfig } from '../../../services/api/apiClient';
import { endPointEnums } from '../../../services/api/endpoints';

export default function useWalletTransactions() {
  const user = useSelector((state) => state.userProfile.data?.user);
  const { makeApiCall } = useApiCall();
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all'); // 'all', 'credit', 'debit'
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const itemsPerPage = 10;

  const fetchTransactions = useCallback(async () => {
    if (!user?.userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const apiConfig = buildApiConfig(endPointEnums.WALLET_TRANSACTIONS, {
        userId: user.userId,
        limit: 500, // Fetch more for client-side pagination and filtering
      });

      const response = await makeApiCall(apiConfig);

      const txns = response?.data?.transactions || [];

      setTransactions(txns);
      setError(null);
    } catch (err) {
      console.error('useWalletTransactions: Error:', err);
      setError(err.message || 'Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  }, [user?.userId, makeApiCall]);

  // Apply filters whenever transactions, filter, or dateRange changes
  useEffect(() => {
    let filtered = [...transactions];

    // Filter by type
    if (filter !== 'all') {
      filtered = filtered.filter((txn) => txn.type === filter);
    }

    // Filter by date range
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter((txn) => {
        const txnDate = new Date(txn.date);
        if (dateRange.start && txnDate < new Date(dateRange.start)) return false;
        if (dateRange.end && txnDate > new Date(dateRange.end)) return false;
        return true;
      });
    }

    setFilteredTransactions(filtered);
    setPage(1); // Reset to first page when filters change
  }, [transactions, filter, dateRange]);

  // Get paginated transactions
  const paginatedTransactions = filteredTransactions.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

  const refresh = useCallback(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const nextPage = useCallback(() => {
    if (page < totalPages) setPage((p) => p + 1);
  }, [page, totalPages]);

  const prevPage = useCallback(() => {
    if (page > 1) setPage((p) => p - 1);
  }, [page]);

  const goToPage = useCallback(
    (pageNum) => {
      setPage(Math.max(1, Math.min(pageNum, totalPages)));
    },
    [totalPages]
  );

  useEffect(() => {
    if (user?.userId) {
      fetchTransactions();
    }
  }, [user?.userId, fetchTransactions]);

  // Auto-refresh when a payment completes (walletUpdated event) or tab regains focus
  useEffect(() => {
    if (!user?.userId) return;

    const handleWalletUpdated = () => {
      fetchTransactions();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchTransactions();
      }
    };

    window.addEventListener('walletUpdated', handleWalletUpdated);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('walletUpdated', handleWalletUpdated);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.userId, fetchTransactions]);

  return {
    transactions: paginatedTransactions,
    allTransactions: filteredTransactions,
    loading,
    error,
    refresh,
    // Pagination
    page,
    totalPages,
    nextPage,
    prevPage,
    goToPage,
    // Filters
    filter,
    setFilter,
    dateRange,
    setDateRange,
    totalCount: filteredTransactions.length,
  };
}
